/**
 * Please refer the following docs for more detals.
 * https://www.100ms.live/docs/javascript/v2/how--to-guides/extend-capabilities/plugins/virtual-background
 */
import { useEffect, useRef, useState } from "react";
import { HMSVirtualBackgroundTypes } from "@100mslive/hms-virtual-background";
import useI18n from "../../components/hooks/useI18n";
import {
  selectIsAllowedToPublish,
  selectIsLocalVideoPluginPresent,
  selectLocalPeerRole,
  selectLocalVideoTrackID,
  useHMSActions,
  useHMSStore,
} from "@100mslive/react-sdk";
import { VirtualBackgroundIcon } from "@100mslive/react-icons";
import { Box, Button, Dialog, Flex, HorizontalDivider, Loading, Text, Tooltip } from "@100mslive/roomkit-react";
import IconButton from "../../IconButton";
import { useIsFeatureEnabled } from "../../components/hooks/useFeatures";
import { getAllVirtualBackgrounds, getVirtualBackgroundById } from "./vbutils";
import { FEATURE_LIST } from "../../common/constants";

export const VirtualBackground = () => {
  const { t } = useI18n("common");
  const pluginRef = useRef(null);
  const hmsActions = useHMSActions();
  const isAllowedToPublish = useHMSStore(selectIsAllowedToPublish);
  const role = useHMSStore(state => state.appData?.role);
  const [isVBLoading, setIsVBLoading] = useState(false);
  const [isVBSupported, setIsVBSupported] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedBackgroundId, setSelectedBackgroundId] = useState(null);
  const localPeerVideoTrackID = useHMSStore(selectLocalVideoTrackID);
  const isVBPresent = useHMSStore(selectIsLocalVideoPluginPresent("@100mslive/hms-virtual-background"));
  const isFeatureEnabled = useIsFeatureEnabled(FEATURE_LIST.VIDEO_PLUGINS);
  const backgrounds = getAllVirtualBackgrounds();
  // The role arrives after the first render, so returning before the hooks below breaks the hook order.
  const isCandidate = role === "candidate" || role?.name === "candidate";

  async function createPlugin() {
    if (!pluginRef.current) {
      try {
        const { HMSVirtualBackgroundPlugin } = await import("@100mslive/hms-virtual-background");
        pluginRef.current = new HMSVirtualBackgroundPlugin(
          HMSVirtualBackgroundTypes.NONE,
          false // enableSharpening
        );
        await pluginRef.current.init();
      } catch (err) {
        throw err;
      }
    }
  }
  useEffect(() => {
    if (!localPeerVideoTrackID || isCandidate) {
      return;
    }
    createPlugin().then(() => {
      //check support of plugin
      const pluginSupport = hmsActions.validateVideoPluginSupport(
        pluginRef.current
      );
      setIsVBSupported(pluginSupport.isSupported);
    });
  }, [hmsActions, localPeerVideoTrackID, isCandidate]);

  async function addPlugin(backgroundId, retryCount = 0) {
    setIsVBLoading(true);
    
    const attemptAddPlugin = async () => {
      await createPlugin();
      
      if (!pluginRef.current) {
        throw new Error("Failed to create virtual background plugin");
      }
      
      window.HMS.virtualBackground = pluginRef.current;
      
      const virtualBackground = await Promise.race([
        getVirtualBackgroundById(backgroundId),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error("Background loading timeout")), 10000)
        )
      ]);
      
      if (!virtualBackground) {
        throw new Error("Selected background not found");
      }
      
      await pluginRef.current.setBackground(virtualBackground.background);
      
      const frameRate = role?.publishParams?.video?.frameRate || 30;
      const pluginFrameRate = Math.floor(frameRate / 2);
      
      await hmsActions.addPluginToVideoTrack(
        pluginRef.current,
        pluginFrameRate
      );
      
      setSelectedBackgroundId(backgroundId);
      setModalOpen(false);
    };

    try {
      await attemptAddPlugin();
    } catch (err) {
      console.error("Virtual background error:", err);
      
      if (retryCount === 0) {
        if (pluginRef.current) {
          try {
            await hmsActions.removePluginFromVideoTrack(pluginRef.current);
          } catch {
          }
          pluginRef.current = null;
        }
        
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        return addPlugin(backgroundId, 1);
      }
      console.error("Virtual background failed after retry:", err);
    } finally {
      setIsVBLoading(false);
    }
  }

  async function removePlugin() {
    setIsVBLoading(true);
    try {
      if (pluginRef.current) {
        await hmsActions.removePluginFromVideoTrack(pluginRef.current);
        pluginRef.current = null;
        if (window.HMS) {
          window.HMS.virtualBackground = null;
        }
      }
      setSelectedBackgroundId(null);
    } catch (err) {
      console.error("Virtual background remove error:", err);
    } finally {
      setIsVBLoading(false);
    }
  }

  if (isCandidate) {
    return null;
  }

  if (!isAllowedToPublish.video || !isVBSupported || !isFeatureEnabled) {
    return null;
  }

  return (
    <>
      <Tooltip
        title={t(isVBLoading ? "adding_virtual_background" : !isVBPresent ? "turn_on_virtual_background" : "turn_off_virtual_background")}
      >
        <IconButton
          active={isVBPresent}
          disabled={isVBLoading}
          onClick={() => {
            if (isVBPresent) {
              removePlugin();
            } else {
              setModalOpen(true);
            }
          }}
          data-testid="virtual_bg_btn"
        >
          {isVBLoading ? <Loading /> : <VirtualBackgroundIcon />}
        </IconButton>
      </Tooltip>

      <Dialog.Root open={modalOpen} onOpenChange={setModalOpen}>
        <Dialog.Portal>
          <Dialog.Overlay />
          <Dialog.Content
            css={{
              width: "min(600px, 90%)",
              maxHeight: "80vh",
              p: "$10",
            }}
          >
            <Dialog.Title css={{ p: 0 }} asChild>
              <Text as="h6" variant="h6">
                {t("turn_on_virtual_background")}
              </Text>
            </Dialog.Title>
            <Dialog.Description asChild>
              <Text
                variant="body2"
                css={{
                  mt: "$4",
                  mb: "$8",
                  c: "$on_surface_medium",
                }}
              >
                {t("select_virtual_background")}
              </Text>
            </Dialog.Description>
            <HorizontalDivider css={{ mt: "0.8rem", mb: "$8" }} />
            <Box
              css={{
                maxHeight: "50vh",
                overflowY: "auto",
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
                gap: "$6",
                mb: "$10",
              }}
            >
              {backgrounds.map(background => {
                const isSelected = background.id === selectedBackgroundId;
                const isBlur = background.backgroundType === HMSVirtualBackgroundTypes.BLUR;
                const isImage = background.backgroundType === HMSVirtualBackgroundTypes.IMAGE;
                const isVideo = background.backgroundType === HMSVirtualBackgroundTypes.VIDEO;
                
                return (
                  <Box
                    key={background.id}
                    onClick={() => setSelectedBackgroundId(background.id)}
                    css={{
                      position: "relative",
                      border: `2px solid ${isSelected ? "$primary_default" : "$border_bright"}`,
                      borderRadius: "$3",
                      cursor: "pointer",
                      bg: isSelected ? "$primary_dim" : "$surface_bright",
                      overflow: "hidden",
                      aspectRatio: "16/9",
                      "&:hover": {
                        borderColor: isSelected ? "$primary_default" : "$on_surface_medium",
                        transform: "scale(1.02)",
                        transition: "all 0.2s",
                      },
                      transition: "all 0.2s",
                    }}
                  >
                    {isBlur ? (
                      <Flex
                        align="center"
                        justify="center"
                        css={{
                          size: "100%",
                          bg: "$surface_brighter",
                          backgroundImage: "radial-gradient(circle, $on_surface_low 1px, transparent 1px)",
                          backgroundSize: "20px 20px",
                        }}
                      >
                        <Text
                          variant="body2"
                          css={{
                            color: "$on_surface_high",
                            fontWeight: "$semiBold",
                          }}
                        >
                          {background.label}
                        </Text>
                      </Flex>
                    ) : isImage ? (
                      <>
                        <Box
                          as="img"
                          src={background.background}
                          alt={background.label}
                          css={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            display: "block",
                          }}
                        />
                        <Box
                          css={{
                            position: "absolute",
                            bottom: 0,
                            left: 0,
                            right: 0,
                            bg: "linear-gradient(to top, rgba(0,0,0,0.7), transparent)",
                            p: "$4",
                          }}
                        >
                          <Text
                            variant="caption"
                            css={{
                              color: "$on_primary_high",
                              fontWeight: "$semiBold",
                            }}
                          >
                            {background.label}
                          </Text>
                        </Box>
                      </>
                    ) : isVideo ? (
                      <>
                        <Box
                          as="video"
                          src={background.background}
                          css={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                          muted
                          loop
                          playsInline
                          autoPlay
                        />
                        <Box
                          css={{
                            position: "absolute",
                            bottom: 0,
                            left: 0,
                            right: 0,
                            bg: "linear-gradient(to top, rgba(0,0,0,0.7), transparent)",
                            p: "$4",
                          }}
                        >
                          <Text
                            variant="caption"
                            css={{
                              color: "$on_primary_high",
                              fontWeight: "$semiBold",
                            }}
                          >
                            {background.label}
                          </Text>
                        </Box>
                      </>
                    ) : null}
                    {isSelected && (
                      <Box
                        css={{
                          position: "absolute",
                          top: "$2",
                          right: "$2",
                          size: "$8",
                          borderRadius: "50%",
                          bg: "$primary_default",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Text css={{ color: "$on_primary_high", fontSize: "$xs" }}>✓</Text>
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
            <Flex
              justify="center"
              align="center"
              css={{ width: "100%", gap: "$md" }}
            >
              <Box css={{ width: "50%" }}>
                <Dialog.Close css={{ width: "100%", p: "$4 $8" }} asChild>
                  <Button
                    variant="standard"
                    outlined
                    css={{ width: "100%" }}
                    data-testid="cancel_vb_button"
                  >
                    {t("cancel")}
                  </Button>
                </Dialog.Close>
              </Box>
              <Box css={{ width: "50%" }}>
                <Button
                  variant="primary"
                  css={{ width: "100%" }}
                  disabled={isVBLoading || !selectedBackgroundId}
                  onClick={() => addPlugin(selectedBackgroundId)}
                  data-testid="apply_vb_button"
                >
                  {isVBLoading ? <Loading /> : t("apply")}
                </Button>
              </Box>
            </Flex>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
};
